import React, { useState } from 'react';
import { QrCode, Download, Copy, Check } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import QRCode from 'qrcode';
import LogoGuidesofia from './LOGO_guidesofia_QR.jpg';
import HeadphoneIcon from './headphone.png';

const loadImage = (source) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('A QR code image asset could not be loaded.'));
  image.src = source;
});

const canvasToPngBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) resolve(blob);
    else reject(new Error('The QR code image could not be created.'));
  }, 'image/png');
});

export const generateQRCodePngBlob = async ({ qrValue, withHeader = false }) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const headerHeight = withHeader ? 300 : 0;
  const footerHeight = 100;
  canvas.width = 1024;
  canvas.height = 1024 + headerHeight + footerHeight;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (withHeader) {
    const headphoneImg = await loadImage(HeadphoneIcon);
    const iconSize = 220;
    const iconX = (canvas.width - iconSize) / 2;
    const iconY = 20;
    ctx.drawImage(headphoneImg, iconX, iconY, iconSize, iconSize);
    ctx.fillStyle = '#077691';
    ctx.font = 'bold 64px Helvetica, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('AUDIO-GUIDE', canvas.width / 2, iconY + iconSize + 35);
  }

  const qrCodeDataUrl = await QRCode.toDataURL(qrValue, {
    width: 1024,
    margin: 2,
    errorCorrectionLevel: 'H',
    color: { dark: '#077691', light: '#FFFFFF' },
  });
  const qrImg = await loadImage(qrCodeDataUrl);
  ctx.drawImage(qrImg, 0, headerHeight, 1024, 1024);

  const logoImg = await loadImage(LogoGuidesofia);
  const logoSize = 380;
  const centerX = (1024 - logoSize) / 2;
  const centerY = headerHeight + ((1024 - logoSize) / 2);
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(centerX, centerY, logoSize, logoSize);
  ctx.drawImage(logoImg, centerX, centerY, logoSize, logoSize);

  ctx.fillStyle = '#808080';
  ctx.font = 'bold 56px Helvetica, Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('SCAN ME', canvas.width / 2, headerHeight + 1024 + (footerHeight / 2));
  return canvasToPngBlob(canvas);
};

export const QRCodeGenerator = ({ qrValue, stopName, className = '', children }) => {
  const [copied, setCopied] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [includeHeader, setIncludeHeader] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(qrValue).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const downloadQRCode = async (withHeader = false) => {
    try {
      const blob = await generateQRCodePngBlob({ qrValue, withHeader });
      const pngUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      const suffix = withHeader ? '_with_header' : '';
      downloadLink.download = `${stopName || 'stop'}_qrcode_1024px${suffix}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      window.setTimeout(() => URL.revokeObjectURL(pngUrl), 30000);
    } catch (error) {
      console.error('Error generating high-resolution QR code:', error);
      // Fallback to current QR code download
      const canvas = document.getElementById(`qrcode-${qrValue}`);
      if (canvas) {
        const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `${stopName || 'stop'}_qrcode.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    }
  };

  return (
    <>
      <button
        onClick={(e) => {
          setMousePosition({ x: e.clientX, y: e.clientY });
          setShowDialog(true);
        }}
        className={`text-teal-500 hover:text-teal-700 flex items-center gap-2 ${className}`}
        title="Generate QR Code"
      >
        <QrCode className="w-5 h-5" />
        {children}
      </button>

      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <div
            className="bg-white rounded-lg shadow-xl p-6 w-96 absolute"
            style={{
              left: '50%',
              top: Math.max(50, Math.min(mousePosition.y, window.innerHeight - 550)),
              transform: 'translate(-50%, 0)'
            }}
          >
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              QR Code for {stopName || 'Stop'}
            </h3>
            
            <div className="flex flex-col items-center mb-4">
              <div className="relative inline-block">
                <QRCodeCanvas
                  id={`qrcode-${qrValue}`}
                  value={qrValue}
                  size={260}
                  level="H"
                  includeMargin={true}
                  className="mb-4"
                  fgColor="#077691"
                  imageSettings={{
                    src: LogoGuidesofia,
                    height: 55,
                    width: 55,
                    excavate: true,
                  }}
                />
              </div>
              
              <div className="text-sm text-gray-600 break-all text-center mb-4">
                {qrValue}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <button
                onClick={() => downloadQRCode(false)}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download QR Code (Standard)
              </button>

              <button
                onClick={() => downloadQRCode(true)}
                className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download with Audio-Guide Header
              </button>

              <button
                onClick={handleCopy}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 flex items-center justify-center gap-2"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy URL'}
              </button>

              <button
                onClick={() => setShowDialog(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QRCodeGenerator;
